// src/pages/Administration/Users/index.tsx
/**
 * `/Administration/Users`: who can use Acts Care.
 *
 * Built from `@/components/ui` and shaped like `/settings`: sticky `Paper` header carrying the actions,
 * gradient `Tabs`, bordered `Paper` cards inside each panel. No direct `@mantine/core` imports.
 *
 * ⚠️ **A person can hold any combination of the three roles**, one, two or all three. They are not
 * mutually exclusive, so every control here is multi-select and every display is a list. An earlier
 * version used a radio group and `find()`, which silently hid a second role and would have wiped it on
 * the next save.
 *
 * Three jobs, one per tab:
 *
 * 1. **Acts Care users**, the roster, drawn as a **table, grid or list** with client-side pagination,
 *    matching the view toggle on `/Information/CareSupportLeaders`. Only people holding at least one of
 *    the three roles.
 *    `getUsers` sends `roles` as repeated query params so the filter is server-side, but the rows are
 *    filtered again on arrival: if the backend ignores `roles`, showing the whole directory would read as
 *    "these are your Acts Care staff". See `_deploy/_newEndPoints/identity-acts-care-users.md` §1.
 * 2. **Add someone**, the search. Any existing CCMS user, given one or more roles. ⚠️
 *    `userService.search` covers name, email and username, **not cellphone**, so a phone number finds
 *    nobody until the backend widens it (§2). The UI says so rather than looking broken.
 * 3. **Needs attention**, the gap. A `Leader` record whose user lacks `ActsCareLeader`, or a
 *    `Facilitator` record whose user lacks `ActsCareFacilitator`. Checked **per role**, so someone who
 *    facilitates courses but was never given the leader role still shows up for their leader record.
 *
 * Roles from other parts of CCMS are deliberately **not shown or edited**.
 *
 * ⚠️ **Role writes send the whole set.** `assignRoles` POSTs `{ roleNames: [...] }` and whether the
 * backend replaces or merges is not visible from the client. A replace would strip every non-Acts-Care
 * role the person holds elsewhere in CCMS, so the save reads current roles first, swaps out **all**
 * Acts Care entries for the chosen ones, and sends the complete result.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  IconAlertTriangle, IconChevronRight, IconLayoutGrid, IconLayoutList, IconPlus, IconRefresh,
  IconSearch, IconShieldCheck, IconTable, IconUserPlus, IconUsers,
} from '@tabler/icons-react';
import {
  Alert, Avatar, Badge, Box, Button, Card, Checkbox, Divider, Drawer, Group, Paper, Progress,
  SearchInput, SimpleGrid, Skeleton, Stack, Text, TextInput, ThemeIcon, Title, Tooltip,
} from '@/components/ui';
import Tabs from '@/components/ui/navigation/Tabs';
import SegmentedControl from '@/components/ui/navigation/SegmentedControl';
import { CCMSDataTable, ListPagination, GRID_PAGE_SIZES, ROW_PAGE_SIZES } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import UserDetailModal from './UserDetailModal';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useAdminService, useUserService, useLeaderService, useFacilitatorService,
  type UserModelView,
} from '@actschurch/ccms-sdk';
import { getInitialsFromName, resolveAvatarSrc } from '@/utils/avatar';

/** The only roles this page grants or removes. A person may hold any combination of them. */
const CARE_ROLES = [
  { value: 'ActsCareAdmin', label: 'Administrator', hint: 'Full access, including Look Up and Administration.' },
  { value: 'ActsCareLeader', label: 'Care Support Leader', hint: 'Works care cases assigned to them.' },
  { value: 'ActsCareFacilitator', label: 'Facilitator', hint: 'Runs course enrolments assigned to them.' },
] as const;

const CARE_ROLE_NAMES: string[] = CARE_ROLES.map((r) => r.value);

const PAGE_SIZE = 100;

/**
 * Safety ceiling on roster paging. Only reached if the backend ignores the `roles` filter, in which case
 * we are walking the whole directory; hitting it is reported rather than silently truncating.
 */
const MAX_ROSTER_PAGES = 25;

/** Nearest allowed size, so switching view keeps roughly the same amount on screen. */
const snapPageSize = (size: number, allowed: number[]) =>
  allowed.reduce((best, n) => (Math.abs(n - size) < Math.abs(best - size) ? n : best), allowed[0]);

/** How many Identity lookups run at once during the access check. Polite, not a limit on coverage. */
const GAP_CHECK_CONCURRENCY = 8;

const roleLabel = (role: string) => CARE_ROLES.find((r) => r.value === role)?.label ?? role;

/** Every Acts Care role the person holds, in the order declared above so badges read consistently. */
const careRolesOf = (roles?: string[] | null): string[] => {
  const held = new Set(roles ?? []);
  return CARE_ROLE_NAMES.filter((r) => held.has(r));
};

const nameOf = (u: UserModelView) =>
  u.fullName?.trim()
  || [u.firstName, u.surname].filter(Boolean).join(' ').trim()
  || u.displayName?.trim()
  || u.email
  || 'Unknown';

/** A leader or facilitator record whose user is missing the matching role. */
interface AccessGap {
  userId: string;
  /** The role they are missing, which is what makes the fix unambiguous. */
  missingRole: string;
  kindLabel: string;
  name: string;
  avatar?: string | null;
  /**
   * Whether an Identity account exists, established by asking `getUserRoles(userId)` rather than
   * inferred from the roster. This is the difference between "give them a role" and "create an
   * account", and getting it wrong tells an admin to create a second account for someone who already
   * has one.
   */
  accountExists: boolean;
  /** Acts Care roles they do hold. Empty is meaningful only when `accountExists`. */
  heldRoles: string[];
}

/**
 * Runs `task` over every item, a few at a time, reporting progress as it goes.
 *
 * Batched rather than capped: an admin cannot act on "we checked some of them", so coverage is complete
 * and the politeness lives in the concurrency instead.
 */
async function inBatches<T, R>(
  items: T[],
  size: number,
  task: (item: T) => Promise<R>,
  onProgress?: (done: number, total: number) => void,
): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(...await Promise.all(items.slice(i, i + size).map(task)));
    onProgress?.(Math.min(i + size, items.length), items.length);
  }
  return out;
}

/** Multi-select over the three roles. Themed `Checkbox` has no `.Group`, so membership is managed here. */
function RolePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (role: string, checked: boolean) =>
    onChange(checked ? [...value, role] : value.filter((r) => r !== role));

  return (
    <Stack gap="sm" mt="xs">
      {CARE_ROLES.map((r) => (
        <Checkbox
          key={r.value}
          label={r.label}
          description={r.hint}
          checked={value.includes(r.value)}
          onChange={(e) => toggle(r.value, e.currentTarget.checked)}
        />
      ))}
    </Stack>
  );
}

function RoleBadges({ roles }: { roles: string[] }) {
  if (roles.length === 0) {
    return <Badge size="xs" variant="light" color="gray">No access</Badge>;
  }
  return (
    <>
      {roles.map((r) => (
        <Badge key={r} size="xs" variant="light" color="blue">{roleLabel(r)}</Badge>
      ))}
    </>
  );
}

function PersonRow({
  name,
  secondary,
  avatar,
  badge,
  right,
  onClick,
}: {
  name: string;
  secondary?: string | null;
  avatar?: string | null;
  badge?: ReactNode;
  right: ReactNode;
  /** When given, the whole row opens the detail popup. */
  onClick?: () => void;
}) {
  const src = resolveAvatarSrc(avatar);
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      gap="md"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={onClick ? { cursor: 'pointer' } : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      } : undefined}
    >
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <Avatar src={src} alt={name} radius="xl" size="md">{getInitialsFromName(name)}</Avatar>
        <Box style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="wrap">
            <Text size="sm" fw={600} truncate>{name}</Text>
            {badge}
          </Group>
          <Text size="xs" c="dimmed" truncate>{secondary || 'No email on record'}</Text>
        </Box>
      </Group>
      <Box style={{ flexShrink: 0 }}>{right}</Box>
    </Group>
  );
}

function RowSkeletons({ count = 3 }: { count?: number }) {
  return (
    <Stack gap="md">
      {Array.from({ length: count }).map((_, i) => (
        <Group key={i} gap="sm">
          <Skeleton circle height={38} />
          <Stack gap={6} style={{ flex: 1 }}>
            <Skeleton height={12} width="30%" radius="sm" />
            <Skeleton height={10} width="45%" radius="sm" />
          </Stack>
        </Group>
      ))}
    </Stack>
  );
}

export default function AdministrationUsersPage() {
  const Admin = useAdminService();
  const Users = useUserService();
  const Leaders = useLeaderService();
  const Facilitators = useFacilitatorService();

  // ── The Acts Care roster ──────────────────────────────────────────────────
  const [careUsers, setCareUsers] = useState<UserModelView[]>([]);
  const [loadingCare, setLoadingCare] = useState(false);
  const [careError, setCareError] = useState<string | null>(null);
  const [rolesUnavailable, setRolesUnavailable] = useState(false);
  const [rosterTruncated, setRosterTruncated] = useState(false);
  const [rosterFilter, setRosterFilter] = useState('');
  /** How the roster is drawn. Table for scanning, grid for faces, list for a compact read. */
  const [rosterView, setRosterView] = useState<'table' | 'grid' | 'list'>('table');
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterPageSize, setRosterPageSize] = useState(12);

  /** The grid needs sizes that divide by the column count; rows do not care. */
  const paginationLayout = rosterView === 'grid' ? 'grid' : 'rows';

  // ── Add someone ───────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<UserModelView[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ── Access gaps ───────────────────────────────────────────────────────────
  const [gaps, setGaps] = useState<AccessGap[]>([]);
  const [loadingGaps, setLoadingGaps] = useState(false);
  const [gapsChecked, setGapsChecked] = useState(false);
  /** Live progress, so a long check shows movement instead of an idle spinner. */
  const [gapProgress, setGapProgress] = useState({ done: 0, total: 0 });
  const [gapFilter, setGapFilter] = useState('');

  // ── Drawers ───────────────────────────────────────────────────────────────
  /** The person whose details popup is open. Every row, card and table line opens this. */
  const [detailTarget, setDetailTarget] = useState<UserModelView | null>(null);
  const [roleTarget, setRoleTarget] = useState<UserModelView | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [savingRole, setSavingRole] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState<{
    email: string; firstName: string; surname: string; cellphone: string; roles: string[];
  }>({ email: '', firstName: '', surname: '', cellphone: '', roles: ['ActsCareLeader'] });

  const loadCareUsers = useCallback(async () => {
    setLoadingCare(true);
    setCareError(null);
    try {
      // ⚠️ **Paged, not a single request.** This used to fetch one page of 100 and stop. If the backend
      // does not apply the `roles` filter, that is the first 100 of the *entire* directory, so anyone
      // sorted after them is simply absent from the roster with no indication why. Keep asking until a
      // short page comes back.
      const all: UserModelView[] = [];
      let page = 1;
      let hitCeiling = false;
      for (;;) {
        const batch = (await Admin.getUsers({
          roles: CARE_ROLE_NAMES,
          page,
          pageSize: PAGE_SIZE,
        })) ?? [];
        all.push(...batch);
        if (batch.length < PAGE_SIZE) break;
        page += 1;
        if (page > MAX_ROSTER_PAGES) { hitCeiling = true; break; }
      }
      setRosterTruncated(hitCeiling);

      // De-duplicate by id: a backend that ignores paging would otherwise repeat page 1 forever.
      const seen = new Set<string>();
      const unique = all.filter((u) => {
        const id = u.id?.trim();
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      const withRoles = unique.filter((u) => careRolesOf(u.roles).length > 0);

      // ⚠️ Rows came back but not one carried a role. That means the payload omits `roles`, not that
      // nobody has one, so filtering on it would wrongly empty the roster. Keep the rows and say the
      // list could not be confirmed, rather than showing nothing or asserting it is Acts Care only.
      const rolesMissing = unique.length > 0 && withRoles.length === 0;
      setRolesUnavailable(rolesMissing);
      setCareUsers(rolesMissing ? unique : withRoles);
    } catch (e) {
      console.error('[Users] failed to load Acts Care users:', e);
      setCareError(e instanceof Error ? e.message : String(e));
      setCareUsers([]);
    } finally {
      setLoadingCare(false);
    }
  }, [Admin]);

  useEffect(() => { loadCareUsers(); }, [loadCareUsers]);

  /**
   * Works out who is set up for care work but cannot sign in and do it.
   *
   * ⚠️ **This asks per person rather than inferring from the roster.** The previous version compared
   * leader/facilitator userIds against the Acts Care roster, which produced nonsense the moment the
   * roster was incomplete: a signed-in administrator appeared under "Create account". Two ways that
   * happened, both outside our control, and neither detectable from the result:
   *   1. the backend ignoring the `roles` filter, and
   *   2. `UserModelView.roles` arriving unpopulated in list responses, which the local safety filter
   *      then treated as "holds no Acts Care role" and dropped.
   *
   * `getUserRoles(userId)` answers both questions definitively: what they hold, and, by failing,
   * whether an account exists at all. That is `hasIdentityUser` from
   * `_deploy/_newEndPoints/identity-acts-care-users.md` §4, bought with N requests instead of an
   * endpoint. It is therefore **on demand** rather than on page load, and it checks **everyone**: a
   * partial answer is not actionable, so the politeness is in the concurrency, not in a cap.
   */
  const loadGaps = useCallback(async () => {
    setLoadingGaps(true);
    setGapsChecked(true);
    setGapProgress({ done: 0, total: 0 });
    try {
      const [leaders, facilitators] = await Promise.all([
        Leaders.list().catch(() => []),
        Facilitators.list().catch(() => []),
      ]);

      /** One entry per person, carrying every role their records require. */
      const required = new Map<string, { name: string; avatar?: string | null; roles: Set<string> }>();
      const want = (
        userId: string | undefined,
        role: string,
        name: string,
        avatar?: string | null,
      ) => {
        const id = userId?.trim();
        if (!id) return;
        const entry = required.get(id) ?? { name, avatar, roles: new Set<string>() };
        entry.roles.add(role);
        if (!entry.name || entry.name === 'Unknown') entry.name = name;
        if (!entry.avatar) entry.avatar = avatar;
        required.set(id, entry);
      };

      leaders.forEach((l) => want(
        l.userId,
        'ActsCareLeader',
        [l.firstName, l.surname].filter(Boolean).join(' ').trim() || l.fullName?.trim() || 'Unknown',
        l.avatar,
      ));
      facilitators.forEach((f) => want(
        f.userId,
        'ActsCareFacilitator',
        [f.firstName, f.surname].filter(Boolean).join(' ').trim() || f.fullName?.trim() || 'Unknown',
        f.avatar,
      ));

      const ids = [...required.keys()];
      setGapProgress({ done: 0, total: ids.length });

      const resolved = await inBatches(ids, GAP_CHECK_CONCURRENCY, async (userId) => {
        try {
          const roles = (await Admin.getUserRoles(userId)) ?? [];
          return { userId, accountExists: true, heldRoles: careRolesOf(roles) };
        } catch {
          // A failure here is most often "no such user", which is exactly the state we are hunting.
          // It could also be a transient error, so the row says the account was not found rather than
          // asserting it does not exist.
          return { userId, accountExists: false, heldRoles: [] as string[] };
        }
      }, (done, total) => setGapProgress({ done, total }));

      const found: AccessGap[] = [];
      resolved.forEach(({ userId, accountExists, heldRoles }) => {
        const entry = required.get(userId);
        if (!entry) return;
        entry.roles.forEach((missingRole) => {
          if (heldRoles.includes(missingRole)) return;
          found.push({
            userId,
            missingRole,
            kindLabel: missingRole === 'ActsCareFacilitator' ? 'Facilitator' : 'Care Support Leader',
            name: entry.name,
            avatar: entry.avatar,
            accountExists,
            heldRoles,
          });
        });
      });

      setGaps(found);
    } catch (e) {
      console.error('[Users] failed to work out access gaps:', e);
      toast.error('Could not run the check', e instanceof Error ? e.message : String(e));
      setGaps([]);
    } finally {
      setLoadingGaps(false);
    }
  }, [Leaders, Facilitators, Admin]);

  const runSearch = useCallback(async () => {
    const term = searchTerm.trim();
    if (!term) { setResults([]); setHasSearched(false); return; }
    setSearching(true);
    setHasSearched(true);
    try {
      setResults((await Users.search({ searchTerm: term, page: 1, pageSize: 25 })) ?? []);
    } catch (e) {
      console.error('[Users] search failed:', e);
      toast.error('Search failed', e instanceof Error ? e.message : String(e));
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [Users, searchTerm]);

  /** Opens the role drawer, optionally pre-ticking a role the person is missing. */
  const openRoleDrawer = (user: UserModelView, alsoTick?: string) => {
    setRoleTarget(user);
    const held = careRolesOf(user.roles);
    setSelectedRoles(alsoTick && !held.includes(alsoTick) ? [...held, alsoTick] : held);
  };

  const handleSaveRole = async () => {
    if (!roleTarget?.id) return;
    setSavingRole(true);
    try {
      // Read what the server holds, not the list row, which may be stale.
      let current: string[] = [];
      try {
        current = (await Admin.getUserRoles(roleTarget.id)) ?? [];
      } catch (e) {
        console.warn('[Users] could not read current roles, falling back to the row:', e);
        current = roleTarget.roles ?? [];
      }
      const preserved = current.filter((r) => !CARE_ROLE_NAMES.includes(r));
      const next = [...preserved, ...selectedRoles];

      await Admin.assignRoles(roleTarget.id, { roleNames: next });
      toast.success(
        'Access updated',
        selectedRoles.length === 0
          ? `Acts Care access removed for ${nameOf(roleTarget)}.`
          : `${nameOf(roleTarget)}: ${selectedRoles.map(roleLabel).join(', ')}.`,
      );
      setRoleTarget(null);
      setResults([]);
      setHasSearched(false);
      setSearchTerm('');
      await loadCareUsers();
    } catch (e) {
      console.error('[Users] role assignment failed:', e);
      toast.error('Could not update access', e instanceof Error ? e.message : String(e));
    } finally {
      setSavingRole(false);
    }
  };

  const canCreate = Boolean(
    newUser.email.trim() && newUser.firstName.trim() && newUser.roles.length > 0,
  );

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      // ⚠️ One call is expected to create the Identity user, the AspNetUserDetail row, the Member and
      // the roles. Only the first is certain. See identity-acts-care-users.md §3.
      await Admin.createUser({
        email: newUser.email.trim(),
        firstName: newUser.firstName.trim(),
        surname: newUser.surname.trim(),
        cellphone: newUser.cellphone.trim() || null,
        isMember: true,
        maritalStatusID: 0,
        roles: newUser.roles,
      });
      toast.success(
        'User created',
        `${newUser.email}: ${newUser.roles.map(roleLabel).join(', ')}.`,
      );
      setCreateOpen(false);
      setNewUser({ email: '', firstName: '', surname: '', cellphone: '', roles: ['ActsCareLeader'] });
      await loadCareUsers();
    } catch (e) {
      console.error('[Users] create failed:', e);
      toast.error('Could not create the user', e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  const visibleRoster = useMemo(() => {
    const t = rosterFilter.trim().toLowerCase();
    if (!t) return careUsers;
    return careUsers.filter((u) =>
      nameOf(u).toLowerCase().includes(t) || (u.email ?? '').toLowerCase().includes(t));
  }, [careUsers, rosterFilter]);

  const visibleGaps = useMemo(() => {
    const t = gapFilter.trim().toLowerCase();
    if (!t) return gaps;
    return gaps.filter((g) =>
      g.name.toLowerCase().includes(t)
      || g.userId.toLowerCase().includes(t)
      || roleLabel(g.missingRole).toLowerCase().includes(t));
  }, [gaps, gapFilter]);

  /** Page 1 whenever the set underneath changes, so you are never left on an empty page. */
  useEffect(() => { setRosterPage(1); }, [rosterFilter, rosterPageSize, rosterView]);

  // Carrying 50 into the grid would leave a two-card row on every page, so snap to the nearest size
  // that fills the columns.
  useEffect(() => {
    setRosterPageSize((current) => {
      const allowed = rosterView === 'grid' ? GRID_PAGE_SIZES : ROW_PAGE_SIZES;
      return allowed.includes(current) ? current : snapPageSize(current, allowed);
    });
  }, [rosterView]);

  const rosterTotal = visibleRoster.length;
  const rosterPages = Math.max(1, Math.ceil(rosterTotal / rosterPageSize));
  useEffect(() => {
    if (rosterPage > rosterPages) setRosterPage(rosterPages);
  }, [rosterPage, rosterPages]);

  const pagedRoster = useMemo(
    () => visibleRoster.slice((rosterPage - 1) * rosterPageSize, rosterPage * rosterPageSize),
    [visibleRoster, rosterPage, rosterPageSize],
  );

  const rosterColumns: TableColumn<UserModelView>[] = [
    {
      key: 'avatar',
      header: '',
      accessor: (u) => u,
      sortable: false,
      width: 60,
      align: 'center',
      render: (_, u) => {
        const name = nameOf(u);
        return (
          <Avatar src={resolveAvatarSrc(u.avatar)} alt={name} radius="xl" size="sm">
            {getInitialsFromName(name)}
          </Avatar>
        );
      },
    },
    {
      key: 'name',
      header: 'Name',
      accessor: (u) => nameOf(u),
      sortable: true,
      render: (value) => <Text size="sm" fw={600}>{value}</Text>,
    },
    {
      key: 'email',
      header: 'Email',
      accessor: (u) => u.email ?? '',
      sortable: true,
      render: (value) => <Text size="sm" c="dimmed">{value || 'No email on record'}</Text>,
    },
    {
      key: 'roles',
      header: 'Roles',
      // Sorts by how many roles they hold, since a set has no natural order.
      accessor: (u) => careRolesOf(u.roles).length,
      sortable: true,
      render: (_, u) => (
        <Group gap={4} wrap="wrap"><RoleBadges roles={careRolesOf(u.roles)} /></Group>
      ),
    },
    {
      key: 'actions',
      header: '',
      accessor: (u) => u,
      sortable: false,
      align: 'right',
      width: 120,
      render: (_, u) => (
        <Button size="compact-sm" variant="subtle" onClick={() => setDetailTarget(u)}>
          Details
        </Button>
      ),
    },
  ];

  const rosterIds = useMemo(
    () => new Set(careUsers.map((u) => u.id).filter(Boolean) as string[]),
    [careUsers],
  );

  return (
    <Stack gap="lg">
      <Paper
        py="md"
        px="lg"
        radius="md"
        withBorder
        style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'var(--mantine-color-body)' }}
      >
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <Box>
            <Title order={2}>Users</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Who can use Acts Care, and in what capacity.
            </Text>
          </Box>
          <Group gap="sm">
            <Button
              variant="default"
              size="sm"
              leftSection={<IconRefresh size={16} />}
              onClick={loadCareUsers}
              loading={loadingCare}
            >
              Reload
            </Button>
            <Button size="sm" leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
              Create user
            </Button>
          </Group>
        </Group>
      </Paper>

      <Tabs tabsVariant="gradient" defaultValue="roster" keepMounted={false}>
        <Tabs.List mb="lg">
          <Tabs.Tab value="roster" leftSection={<IconUsers size={16} />}>
            Acts Care users
          </Tabs.Tab>
          <Tabs.Tab value="add" leftSection={<IconUserPlus size={16} />}>
            Add someone
          </Tabs.Tab>
          <Tabs.Tab
            value="gaps"
            leftSection={<IconAlertTriangle size={16} />}
            rightSection={gaps.length > 0
              ? <Badge size="xs" variant="light" color="orange">{gaps.length}</Badge>
              : undefined}
          >
            Needs attention
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Roster ── */}
        <Tabs.Panel value="roster">
          <Paper p="lg" radius="md" withBorder>
            <Group gap="sm" mb="md" wrap="nowrap">
              <ThemeIcon size="lg" radius="md" variant="light" color="blue">
                <IconShieldCheck size={20} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text fw={600}>Acts Care users</Text>
                <Text size="xs" c="dimmed">
                  Everyone holding at least one Acts Care role. Roles from other parts of CCMS are not
                  shown or changed here.
                </Text>
              </Box>
            </Group>

            <Group justify="space-between" align="center" wrap="wrap" gap="sm" mb="md">
              <SearchInput
                placeholder="Filter this list"
                value={rosterFilter}
                onChange={setRosterFilter}
                onClear={() => setRosterFilter('')}
                radius="md"
                size="sm"
                w={280}
              />
              <SegmentedControl
                size="sm"
                value={rosterView}
                onChange={(v) => setRosterView(v as typeof rosterView)}
                data={[
                  {
                    value: 'table',
                    label: (
                      <Tooltip label="Table"><Group gap={6} wrap="nowrap"><IconTable size={16} /></Group></Tooltip>
                    ),
                  },
                  {
                    value: 'grid',
                    label: (
                      <Tooltip label="Grid"><Group gap={6} wrap="nowrap"><IconLayoutGrid size={16} /></Group></Tooltip>
                    ),
                  },
                  {
                    value: 'list',
                    label: (
                      <Tooltip label="List"><Group gap={6} wrap="nowrap"><IconLayoutList size={16} /></Group></Tooltip>
                    ),
                  },
                ]}
              />
            </Group>

            <Divider mb="md" />

            {rosterTruncated && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                type="warning"
                title="The list may be incomplete"
                mb="md"
              >
                <Text size="sm">
                  Paging stopped after {MAX_ROSTER_PAGES * PAGE_SIZE} users. That only happens when the
                  server is not narrowing the list to Acts Care roles, so use &quot;Add someone&quot; to
                  look up anybody missing here.
                </Text>
              </Alert>
            )}

            {rolesUnavailable && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                type="warning"
                title="Roles were not returned"
                mb="md"
              >
                <Text size="sm">
                  The user list came back without role information, so this cannot be confirmed as Acts
                  Care staff only. Open someone to see the roles they actually hold.
                </Text>
              </Alert>
            )}

            {careError ? (
              <Alert icon={<IconAlertTriangle size={16} />} type="error" title="Could not load users">
                <Stack gap="xs" align="flex-start">
                  <Text size="sm">{careError}</Text>
                  <Button size="xs" variant="light" onClick={loadCareUsers}>Try again</Button>
                </Stack>
              </Alert>
            ) : loadingCare ? (
              <RowSkeletons count={4} />
            ) : visibleRoster.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                {careUsers.length === 0
                  ? 'Nobody has an Acts Care role yet. Use "Add someone" to grant one.'
                  : 'Nobody on the list matches that filter.'}
              </Text>
            ) : (
              <>
                {rosterView === 'table' && (
                  <CCMSDataTable
                    data={pagedRoster}
                    columns={rosterColumns}
                    sortable
                    striped
                    highlightOnHover
                    onRowClick={(user) => setDetailTarget(user)}
                  />
                )}

                {rosterView === 'grid' && (
                  <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                    {pagedRoster.map((user) => {
                      const name = nameOf(user);
                      return (
                        // ⚠️ The wrapper exists for **keyboard** access only. `ThemedCardProps` declares
                        // `onClick` but not `role` / `tabIndex` / `onKeyDown`, and themed `Box` takes
                        // none of them, so a plain div carries the focus and Enter/Space handling.
                        <div
                          key={user.id!}
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setDetailTarget(user)}
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setDetailTarget(user);
                            }
                          }}
                        >
                        {/* No `onClick` here: the wrapper already handles it, and both firing would
                            open the popup twice. The Card only supplies the hover affordance. */}
                        <Card variant="outlined" radius="md" padding="md" h="100%" hoverEffect="border">
                          <Stack gap="xs" align="center" style={{ height: '100%' }}>
                            <Avatar src={resolveAvatarSrc(user.avatar)} alt={name} radius="xl" size={56}>
                              {getInitialsFromName(name)}
                            </Avatar>
                            {/* `ta` on each Text, not `textAlign` on the parent: Mantine's `truncate`
                                sets its own display rules and the inherited alignment did not apply. */}
                            <Box style={{ minWidth: 0, width: '100%' }}>
                              <Text size="sm" fw={600} ta="center" truncate>{name}</Text>
                              <Text size="xs" c="dimmed" ta="center" truncate>
                                {user.email || 'No email on record'}
                              </Text>
                            </Box>
                            <Group gap={4} justify="center" wrap="wrap" mt={2}>
                              <RoleBadges roles={careRolesOf(user.roles)} />
                            </Group>
                            {/* Kept alongside the hover border, because hover does not exist on touch. */}
                            <Group gap={4} justify="center" wrap="nowrap" mt="auto" pt="xs">
                              <Text size="xs" c="dimmed">View details</Text>
                              <IconChevronRight size={12} opacity={0.6} />
                            </Group>
                          </Stack>
                        </Card>
                        </div>
                      );
                    })}
                  </SimpleGrid>
                )}

                {rosterView === 'list' && (
                  <Stack gap="sm">
                    {pagedRoster.map((user) => (
                      <Card
                        key={user.id!}
                        variant="outlined"
                        radius="md"
                        padding="md"
                        hoverEffect="border"
                      >
                        <PersonRow
                          name={nameOf(user)}
                          secondary={user.email}
                          avatar={user.avatar}
                          badge={<RoleBadges roles={careRolesOf(user.roles)} />}
                          onClick={() => setDetailTarget(user)}
                          right={(
                            <Group gap={4} wrap="nowrap">
                              <Text size="xs" c="dimmed">View details</Text>
                              <IconChevronRight size={12} opacity={0.6} />
                            </Group>
                          )}
                        />
                      </Card>
                    ))}
                  </Stack>
                )}

                <ListPagination
                  page={rosterPage}
                  pageSize={rosterPageSize}
                  totalItems={rosterTotal}
                  onPageChange={setRosterPage}
                  onPageSizeChange={(size) => { setRosterPageSize(size); setRosterPage(1); }}
                  layout={paginationLayout}
                  showPageSize
                />
              </>
            )}
          </Paper>
        </Tabs.Panel>

        {/* ── Add someone ── */}
        <Tabs.Panel value="add">
          <Paper p="lg" radius="md" withBorder>
            <Group gap="sm" mb="md" wrap="nowrap">
              <ThemeIcon size="lg" radius="md" variant="light" color="green">
                <IconUserPlus size={20} />
              </ThemeIcon>
              <Box>
                <Text fw={600}>Add an existing user</Text>
                <Text size="xs" c="dimmed">
                  Find someone already in CCMS and give them one or more Acts Care roles.
                </Text>
              </Box>
            </Group>

            <Group gap="sm" mb="xs" wrap="nowrap">
              <TextInput
                style={{ flex: 1 }}
                placeholder="Name, email or cellphone"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                leftSection={<IconSearch size={16} />}
                radius="md"
              />
              <Button onClick={runSearch} loading={searching} disabled={!searchTerm.trim()}>
                Search
              </Button>
            </Group>
            {/* Honest about a real backend limitation rather than looking broken. */}
            <Text size="xs" c="dimmed" mb="md">
              Searches name, email and username. Cellphone is not searched yet.
            </Text>

            <Divider mb="md" />

            {searching ? (
              <RowSkeletons />
            ) : !hasSearched ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                Search for someone to get started.
              </Text>
            ) : results.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                No users match that search.
              </Text>
            ) : (
              <Stack gap="md">
                {results.map((user) => {
                  const already = user.id ? rosterIds.has(user.id) : false;
                  return (
                    <PersonRow
                      key={user.id!}
                      name={nameOf(user)}
                      secondary={user.email}
                      avatar={user.avatar}
                      badge={already ? <RoleBadges roles={careRolesOf(user.roles)} /> : undefined}
                      onClick={() => setDetailTarget(user)}
                      right={
                        <Button
                          size="compact-sm"
                          variant={already ? 'subtle' : 'light'}
                          // The row opens details; this is the direct action, so it must not do both.
                          onClick={(e) => { e.stopPropagation(); openRoleDrawer(user); }}
                        >
                          {already ? 'Change roles' : 'Add to Acts Care'}
                        </Button>
                      }
                    />
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Tabs.Panel>

        {/* ── Access gaps ── */}
        <Tabs.Panel value="gaps">
          <Paper p="lg" radius="md" withBorder>
            <Group gap="sm" mb="md" wrap="nowrap">
              <ThemeIcon size="lg" radius="md" variant="light" color="orange">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text fw={600}>Leaders and facilitators missing their role</Text>
                <Text size="xs" c="dimmed">
                  Set up to do care work but without the matching Acts Care role, so they cannot sign in
                  and do it.
                </Text>
              </Box>
              <Button
                size="compact-sm"
                leftSection={<IconRefresh size={14} />}
                onClick={loadGaps}
                loading={loadingGaps}
              >
                {gapsChecked ? 'Check again' : 'Run check'}
              </Button>
            </Group>

            <Divider mb="md" />

            {loadingGaps ? (
              <Stack gap="md">
                <Text size="xs" c="dimmed">
                  {gapProgress.total > 0
                    ? `Checking ${gapProgress.done} of ${gapProgress.total} against Identity…`
                    : 'Gathering leaders and facilitators…'}
                </Text>
                {gapProgress.total > 0 && (
                  <Progress
                    value={Math.round((gapProgress.done / gapProgress.total) * 100)}
                    size="sm"
                    radius="xl"
                  />
                )}
                <RowSkeletons />
              </Stack>
            ) : !gapsChecked ? (
              <Stack gap="sm" align="center" py="xl">
                <Text size="sm" c="dimmed" ta="center" maw={520}>
                  This asks Identity about every leader and facilitator individually, one request each,
                  so it runs only when you ask for it. It checks all of them, not a sample.
                </Text>
                <Button variant="light" onClick={loadGaps}>Run check</Button>
              </Stack>
            ) : gaps.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                All {gapProgress.total} leaders and facilitators have the role their record needs.
              </Text>
            ) : (
              <Stack gap="md">
                <Text size="xs" c="dimmed">
                  {gaps.length} of {gapProgress.total} checked{' '}
                  {gapProgress.total === 1 ? 'person needs' : 'people need'} attention.
                </Text>

                <SearchInput
                  placeholder="Filter by name or user id"
                  value={gapFilter}
                  onChange={setGapFilter}
                  onClear={() => setGapFilter('')}
                  radius="md"
                  size="sm"
                />

                {visibleGaps.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="lg">
                    Nobody matches that filter.
                  </Text>
                ) : visibleGaps.map((gap) => (
                  <PersonRow
                    key={`${gap.missingRole}-${gap.userId}`}
                    name={gap.name}
                    secondary={gap.accountExists
                      ? (gap.heldRoles.length > 0
                        ? `Holds ${gap.heldRoles.map(roleLabel).join(', ')}`
                        : 'Has an account, no Acts Care roles')
                      : gap.userId}
                    avatar={gap.avatar}
                    badge={(
                      <>
                        <Badge size="xs" variant="light" color="orange">
                          Needs {roleLabel(gap.missingRole)}
                        </Badge>
                        {!gap.accountExists && (
                          <Badge size="xs" variant="light" color="red">No account found</Badge>
                        )}
                      </>
                    )}
                    right={gap.accountExists ? (
                      <Button
                        size="compact-sm"
                        variant="light"
                        onClick={() => {
                          // The roster may not hold this person, since they may have no Acts Care role
                          // at all. Fall back to a minimal stand-in so the drawer can still save.
                          const known = careUsers.find((u) => u.id === gap.userId);
                          openRoleDrawer(
                            known ?? {
                              id: gap.userId,
                              fullName: gap.name,
                              roles: gap.heldRoles,
                              isMember: false,
                              emailConfirmed: false,
                              lockoutEnabled: false,
                              totalRoles: gap.heldRoles.length,
                              totalRights: 0,
                              isLockedOut: false,
                            } as UserModelView,
                            gap.missingRole,
                          );
                        }}
                      >
                        Add role
                      </Button>
                    ) : (
                      <Button
                        size="compact-sm"
                        variant="light"
                        onClick={() => {
                          setNewUser((u) => ({
                            ...u,
                            firstName: gap.name.split(' ')[0] ?? '',
                            surname: gap.name.split(' ').slice(1).join(' '),
                            roles: [gap.missingRole],
                          }));
                          setCreateOpen(true);
                        }}
                      >
                        Create account
                      </Button>
                    )}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>

      <UserDetailModal
        user={detailTarget}
        onClose={() => setDetailTarget(null)}
        onChangeRoles={(u) => openRoleDrawer(u)}
        renderRoles={(u) => <RoleBadges roles={careRolesOf(u.roles)} />}
        displayName={nameOf}
      />

      {/* ── Roles drawer ── */}
      <Drawer
        opened={Boolean(roleTarget)}
        onClose={() => setRoleTarget(null)}
        title="Acts Care roles"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          <Box>
            <Text size="sm" fw={600}>{roleTarget ? nameOf(roleTarget) : ''}</Text>
            <Text size="xs" c="dimmed">{roleTarget?.email ?? ''}</Text>
          </Box>
          <Text size="sm">
            Tick every role this person should have. Roles from other parts of CCMS are left untouched.
          </Text>
          <RolePicker value={selectedRoles} onChange={setSelectedRoles} />
          {selectedRoles.length === 0 && (
            <Text size="xs" c="orange">
              With nothing ticked, saving removes their Acts Care access. The account itself stays.
            </Text>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setRoleTarget(null)} disabled={savingRole}>
              Cancel
            </Button>
            <Button onClick={handleSaveRole} loading={savingRole}>Save</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* ── Create drawer ── */}
      <Drawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create user"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          <TextInput
            label="Email"
            required
            type="email"
            description="Used as the sign-in address."
            value={newUser.email}
            onChange={(e) => setNewUser((u) => ({ ...u, email: e.currentTarget.value }))}
          />
          <TextInput
            label="First name"
            required
            value={newUser.firstName}
            onChange={(e) => setNewUser((u) => ({ ...u, firstName: e.currentTarget.value }))}
          />
          <TextInput
            label="Surname"
            value={newUser.surname}
            onChange={(e) => setNewUser((u) => ({ ...u, surname: e.currentTarget.value }))}
          />
          <TextInput
            label="Cellphone"
            value={newUser.cellphone}
            onChange={(e) => setNewUser((u) => ({ ...u, cellphone: e.currentTarget.value }))}
          />

          <Box>
            <Text size="sm" fw={500}>Acts Care roles</Text>
            <Text size="xs" c="dimmed">At least one. A person may hold any combination.</Text>
            <RolePicker
              value={newUser.roles}
              onChange={(roles) => setNewUser((u) => ({ ...u, roles }))}
            />
          </Box>

          <Text size="xs" c="dimmed">
            Creates the sign-in account, the member profile and the roles together.
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={creating} disabled={!canCreate}>
              Create
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </Stack>
  );
}
